import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReportType } from '@work4all/components/lib/components/entity-picker/report-type-picker/ReportTypePicker';
import { BaseActionButton } from '@work4all/components/lib/input/base-action-button/BaseActionButton';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { useUser } from '@work4all/data';

import { ERPTypes } from '@work4all/models/lib/Classes/ERPTypes.entity';
import { Report } from '@work4all/models/lib/Classes/Report.entity';
import { Report2 } from '@work4all/models/lib/Classes/Report2.entity';
import { ReportOptionsValue } from '@work4all/models/lib/Classes/ReportOptionsValue.entity';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ReportBzObjType } from '@work4all/models/lib/Enums/ReportBzObjType.enum';

import { Report2PickerField } from '../../../../../../../../../../components/entity-picker/Report2PickerField';
import { ReportPickerField } from '../../../../../../../../../../components/entity-picker/ReportPickerField';
import { ReportTypePickerField } from '../../../../../../../../../../components/entity-picker/ReportTypePickerField';
import { SignatureConf } from '../../../../simple-pdf-report/components/signature-conf/SignatureConf';
import { SimpleReportConf } from '../../../../simple-pdf-report/SimpleReportConf';
import { ConvertInvoiceToRaAbilitySwitchButton } from '../../convert-invoice-to-ra';
import { CrystalReportOptionsConf } from '../../crystal-report-options-conf/CrystalReportOptionsConf';
import { DevExpressReportOptionsConf } from '../../devexpress-report-options-conf/DevExpressReportOptionsConf';

interface IProps {
  reportType: ReportType;
  onReportTypeChange: (value: ReportType) => void;
  report: Report | Report2;
  reportBzObjectType: ReportBzObjType;
  setReport: (report: Report | Report2) => void;
  fileName: string;
  setFileName: (value: string) => void;
  reportId: number | string;
  entityType: Entities;
  bzObjectType: BzObjType;
  reportOptionValues: Record<number, boolean>;
  devExpressReportOptionValues: Record<string, string>;
  onChangeReportOptionValues: (optId: number, value: boolean) => void;
  onChangeDevExpressReportOptionValues: (optId: string, value: string) => void;
  data: ERPTypes;
  showSimpleReportConf: boolean;
  setShowSimpleReportConf: (value: boolean) => void;
  errorComponent: React.ReactElement;
  onDevExpressReportOptionsChange: (value?: ReportOptionsValue[]) => void;
}

export const ReportPreviewConfPanel = (props: IProps) => {
  const {
    reportType,
    errorComponent,
    onReportTypeChange,
    setShowSimpleReportConf,
    data,
    showSimpleReportConf,
    reportOptionValues,
    onChangeReportOptionValues,
    report,
    bzObjectType,
    reportBzObjectType,
    setReport,
    fileName,
    setFileName,
    entityType,
    onDevExpressReportOptionsChange,
    devExpressReportOptionValues,
    onChangeDevExpressReportOptionValues,
  } = props;

  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'));

  const { t } = useTranslation();
  const user = useUser();

  return (
    <Box p="1rem">
      <Stack gap="1rem" width="22rem">
        <ReportTypePickerField
          clearable={false}
          value={reportType}
          onChange={onReportTypeChange}
        />
        {reportType === ReportType.CrystalReports && (
          <ReportPickerField
            reportBzObjectType={reportBzObjectType}
            value={report as Report}
            onChange={(report) => {
              setReport(report);
            }}
          />
        )}
        {reportType === ReportType.DevExpress && (
          <Report2PickerField
            reportBzObjectType={reportBzObjectType}
            value={report as Report2}
            onChange={(report) => {
              setReport(report);
            }}
          />
        )}

        <LabeledInput
          value={fileName.split('.pdf')[0]}
          endAdornment={
            <Box pt="1rem">
              <Typography color="var(--text03)">.pdf</Typography>
            </Box>
          }
          inputProps={{
            style: {
              // textAlign: 'right',
            },
          }}
          label={t('COMMON.FILE')}
          onChange={(e) => {
            setFileName(e.target.value.trim() + '.pdf');
          }}
        />

        {entityType === Entities.invoice && (
          <ConvertInvoiceToRaAbilitySwitchButton disabled={data?.ra} />
        )}

        {reportType === ReportType.CrystalReports && (
          <CrystalReportOptionsConf
            options={(report as Report)?.reportOptions}
            bzObjType={bzObjectType}
            reportId={(report as Report)?.id}
            reportOptionValues={reportOptionValues}
            onChangeReportOptionValues={onChangeReportOptionValues}
          />
        )}

        {reportType === ReportType.DevExpress && (
          <DevExpressReportOptionsConf
            options={(report as Report2)?.options}
            onOptionsChange={(e) => {
              onDevExpressReportOptionsChange(e);
            }}
            bzObjType={bzObjectType}
            report={report as Report2}
            reportOptionValues={devExpressReportOptionValues}
            onChangeDevExpressReportOptionValues={
              onChangeDevExpressReportOptionValues
            }
          />
        )}

        {reportType === ReportType.Simple && user.isMaster && (
          <>
            {bzObjectType === BzObjType.LIEFERSCHEIN && data?.signature && (
              <SignatureConf bzObj={data} />
            )}
            <Box>
              <BaseActionButton
                icon={
                  showSimpleReportConf ? (
                    <KeyboardArrowUp />
                  ) : (
                    <KeyboardArrowDown />
                  )
                }
                onClick={() => setShowSimpleReportConf(!showSimpleReportConf)}
              >
                {t(
                  showSimpleReportConf
                    ? 'SIMPLE_REPORT.HIDE_CONF'
                    : 'SIMPLE_REPORT.SHOW_CONF'
                )}
              </BaseActionButton>
            </Box>

            {showSimpleReportConf ? (
              <SimpleReportConf bzObjType={bzObjectType} />
            ) : null}
          </>
        )}
      </Stack>
      {isDesktop && errorComponent}
    </Box>
  );
};
